import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alterarStatusVagaoEstaleiro,
  cadastrarVagaoEstaleiro,
  editarVagaoEstaleiro,
  excluirVagaoEstaleiro,
  listarVagoesEstaleiro,
  resumirVagoesEstaleiro,
} from "./vagoesEstaleiro.api";
import CriarVagaoModal from "./popups/CriarVagaoModal";
import EditarVagaoModal from "./popups/EditarVagaoModal";
import ExcluirVagaoModal from "./popups/ExcluirVagaoModal";
import AtivarVagaoModal from "./popups/AtivarVagaoModal";
import InativarVagaoModal from "./popups/InativarVagaoModal";
import { useDebouncedValue, useInfiniteBaseList } from "../shared/useInfiniteBaseList";
import "./VagoesBase.css";

const toUpperTrim = (value, max = 40) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, max);

const toUpperCode = (value, max = 40) =>
  toUpperTrim(value, max)
    .replace(/\s+/g, "")
    .replace(/[^A-Z0-9]/g, "");

const safeText = (value) => String(value ?? "").trim();
const formatVagaoLabel = (row) =>
  safeText(row?.codigo_vagao) ||
  safeText(row?.codigo_vagao_completo) ||
  `${safeText(row?.serie_vagao)} ${safeText(row?.numero_vagao)}`.trim();

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`bep-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="bep-toggle-knob" />
    </button>
  );
}

export default function VagoesBase() {
  const [feedback, setFeedback] = useState("");

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);
  const tableScrollRef = useRef(null);
  const debouncedFiltroTexto = useDebouncedValue(String(filtroTexto ?? "").trim(), 260);
  const [statsRefreshSeq, setStatsRefreshSeq] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    ativos: 0,
    inativos: 0,
  });

  const { rows, loading, loadingMore, hasMore, error, reload, loadMore } = useInfiniteBaseList({
    listFn: listarVagoesEstaleiro,
    queryText: filtroTexto,
    statusFilter: filtroStatus,
    pageSize: 100,
  });

  useEffect(() => {
    let cancelled = false;

    const carregarTotais = async () => {
      try {
        const resumo = await resumirVagoesEstaleiro({ q: debouncedFiltroTexto });
        if (cancelled) return;

        if (filtroStatus === "ativos") {
          setStats({ total: resumo.ativos, ativos: resumo.ativos, inativos: 0 });
          return;
        }

        if (filtroStatus === "inativos") {
          setStats({ total: resumo.inativos, ativos: 0, inativos: resumo.inativos });
          return;
        }

        setStats(resumo);
      } catch {
        if (!cancelled) {
          setStats({ total: 0, ativos: 0, inativos: 0 });
        }
      }
    };

    carregarTotais();
    return () => {
      cancelled = true;
    };
  }, [debouncedFiltroTexto, filtroStatus, statsRefreshSeq]);

  const rowsOrdenadas = useMemo(() => {
    const sortByText = (a, b) => safeText(a).localeCompare(safeText(b), "pt-BR", { sensitivity: "base" });
    return [...rows].sort((a, b) => sortByText(formatVagaoLabel(a), formatVagaoLabel(b)));
  }, [rows]);

  const handleTableScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      loadMore();
    }
  }, [rows.length, loading, loadingMore, hasMore, loadMore]);

  async function criarVagao({ serieVagao, codigoVagao }) {
    const serie = toUpperTrim(serieVagao, 10);
    const codigo = toUpperCode(codigoVagao, 40);
    await cadastrarVagaoEstaleiro({ serieVagao: serie, codigoVagao: codigo });
    setFeedback("Vagão cadastrado com sucesso.");
    reload();
    setStatsRefreshSeq((v) => v + 1);
  }

  async function editarVagao({ serieVagao, codigoVagao }) {
    if (!rowSelecionada?.id) throw new Error("Vagão selecionado inválido.");
    const serie = toUpperTrim(serieVagao, 10);
    const codigo = toUpperCode(codigoVagao, 40);
    await editarVagaoEstaleiro(rowSelecionada.id, { serieVagao: serie, codigoVagao: codigo });
    setFeedback("Vagão atualizado com sucesso.");
    reload();
    setStatsRefreshSeq((v) => v + 1);
  }

  async function excluirVagao() {
    if (!rowSelecionada?.id) throw new Error("Vagão selecionado inválido.");
    await excluirVagaoEstaleiro(rowSelecionada.id);
    setFeedback("Vagão excluído com sucesso.");
    reload();
    setStatsRefreshSeq((v) => v + 1);
  }

  async function ativarVagao() {
    if (!rowSelecionada?.id) throw new Error("Vagão selecionado inválido.");
    await alterarStatusVagaoEstaleiro(rowSelecionada.id, 1);
    setFeedback("Vagão ativado com sucesso.");
    reload();
    setStatsRefreshSeq((v) => v + 1);
  }

  async function inativarVagao() {
    if (!rowSelecionada?.id) throw new Error("Vagão selecionado inválido.");
    await alterarStatusVagaoEstaleiro(rowSelecionada.id, 0);
    setFeedback("Vagão inativado com sucesso.");
    reload();
    setStatsRefreshSeq((v) => v + 1);
  }

  return (
    <div className="bep-base">
      <div className="bep-cards">
        <div className="bep-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="bep-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="bep-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <div className="bep-toolbar">
        <input
          type="text"
          value={filtroTexto}
          onChange={(event) => setFiltroTexto(event.target.value)}
          placeholder="Buscar série, número ou código do vagão..."
        />
        <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
          <option value="todos">Todos</option>
          <option value="ativos">Somente ativos</option>
          <option value="inativos">Somente inativos</option>
        </select>
        <button
          type="button"
          className="bep-btn bep-btn--primary"
          onClick={() => {
            setFeedback("");
            setOpenCriar(true);
          }}
        >
          Cadastrar vagão
        </button>
      </div>

      <div className="bep-table-wrap">
        <table className="bep-table bep-table--head">
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "30%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Código do vagão</th>
              <th>Série</th>
              <th>Número</th>
              <th>Controles</th>
            </tr>
          </thead>
        </table>

        <div className="bep-table-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
          <table className="bep-table bep-table--body">
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="bep-empty">Carregando base de vagões...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="bep-empty bep-empty--error">{error}</td>
                </tr>
              ) : !rowsOrdenadas.length ? (
                <tr>
                  <td colSpan={4} className="bep-empty">Nenhum vagão encontrado.</td>
                </tr>
              ) : (
                rowsOrdenadas.map((row) => {
                  const ativo = Number(row?.ativo) === 1;
                  return (
                    <tr key={row.id}>
                      <td>{formatVagaoLabel(row)}</td>
                      <td>{safeText(row?.serie_vagao)}</td>
                      <td>{safeText(row?.numero_vagao)}</td>
                      <td>
                        <div className="bep-actions">
                          <ToggleSwitch
                            on={ativo}
                            title={ativo ? "Inativar vagão" : "Ativar vagão"}
                            onToggle={() => {
                              setRowSelecionada(row);
                              if (ativo) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                          />
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Editar vagão"
                            onClick={() => {
                              setFeedback("");
                              setRowSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Excluir vagão"
                            onClick={() => {
                              setRowSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && !error && loadingMore ? (
                <tr>
                  <td colSpan={4} className="bep-empty">Carregando mais...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {feedback ? <p className="bep-feedback">{feedback}</p> : null}

      <CriarVagaoModal
        open={openCriar}
        onClose={() => setOpenCriar(false)}
        onSalvar={criarVagao}
      />

      <EditarVagaoModal
        open={openEditar}
        serieInicial={safeText(rowSelecionada?.serie_vagao)}
        codigoInicial={safeText(rowSelecionada?.numero_vagao)}
        onClose={() => {
          setOpenEditar(false);
          setRowSelecionada(null);
        }}
        onSalvar={editarVagao}
      />

      <ExcluirVagaoModal
        open={openExcluir}
        titulo={formatVagaoLabel(rowSelecionada)}
        onClose={() => {
          setOpenExcluir(false);
          setRowSelecionada(null);
        }}
        onConfirmar={excluirVagao}
      />

      <AtivarVagaoModal
        open={openAtivar}
        titulo={formatVagaoLabel(rowSelecionada)}
        onClose={() => {
          setOpenAtivar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={ativarVagao}
      />

      <InativarVagaoModal
        open={openInativar}
        titulo={formatVagaoLabel(rowSelecionada)}
        onClose={() => {
          setOpenInativar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={inativarVagao}
      />
    </div>
  );
}
